import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Backdrop,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";

const useStyles = makeStyles(() => ({

  paper: {
    width: 639,
    minHeight: 192,
    maxWidth: "calc(100vw - 32px)",
    boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.2)",
    borderRadius: 4,
    overflow: "hidden",
    fontFamily: '"Montserrat", sans-serif !important',
    "& *": {
      fontFamily: '"Montserrat", sans-serif !important',
    },
  },

  /* ---------- HEADER ---------- */
  header: {
    boxSizing: "border-box",
    height: 60,
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 20,
    lineHeight: "24px",
    color: "#0F0F19",
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 24,
    padding: 8,
    color: "#00445D",
  },
  divider: {
    background: "#E6E7EB",
  },

  /* ---------- CONTENT ---------- */
  content: {
    padding: 0,
  },
  confirmSection: {
    boxSizing: "border-box",
    background: "#F9FCFF",
    borderTop: "1px solid #99CCDF",
    borderBottom: "1px solid #99CCDF",
    padding: "16px 16px 24px",
  },

  select: {
    width: "100%",
    background: "#fff",
    border: "1px solid #B6B6B9",
    borderRadius: 4,
    minHeight: 40,
    height: 40,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: "12px",
    color: "#6E6E72",
    "& .MuiSelect-select": {
      padding: "12px 8px",
      display: "flex",
      alignItems: "center",
      minHeight: 16,
    },
    "& .MuiSelect-icon": {
      width: 16,
      height: 16,
      color: "#6E6E72",
      right: 8,
    },
    "& fieldset": {
      border: "none",
    },
  },
  menuItem: {
    display: "flex",
    justifyContent: "space-between",
  },
  checkIcon: {
    color: "#0E4B5B",
    fontSize: 16,
    marginLeft: 8,
  },

  /* ---------- ACTIONS ---------- */
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    height: 52,
    padding: "8px 16px",
    background: "#FFFFFF",
    boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.12)",
  },
  primary: {
    width: 78,
    height: 36,
    textTransform: "capitalize",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: "14px",
    color: "#fff",
    background: "#00445D",
    borderRadius: 999,
    padding: "10px 16px",
    "&:hover": { background: "#00384C" },
    "&.Mui-disabled": {
      background: "#00445D",
      color: "#FFFFFF",
      opacity: 1,
    },
  },
}));

export default function ChangeCompanyModal({
  open,
  onClose,
  currentCompany,
  companies = [],
  onSubmit,
  loading = false,
}) {
  const classes = useStyles();
  const [selectedCompany, setSelectedCompany] = React.useState(currentCompany);

  React.useEffect(() => {
    if (open) {
      setSelectedCompany(currentCompany);
    }
  }, [open, currentCompany]);

  const handleSubmit = () => {
    onSubmit(selectedCompany);
  };

  return (
    <>
      <Backdrop open={loading} style={{ color: "#0E4B5B", zIndex: 2000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.paper }}
      >
        {/* Header */}
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>Change Company</Typography>

          <IconButton className={classes.closeBtn} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider className={classes.divider} />

        {/* Content */}
        <DialogContent className={classes.content}>
          <Box className={classes.confirmSection}>

            <FormControl fullWidth>
              <Select
                value={selectedCompany || ""}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className={classes.select}
                variant="outlined"
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return "Select Company";
                  }
                  const activeCompany = companies.find(
                    (company) => company.companyReference === selected
                  );
                  return activeCompany?.companyName || selected;
                }}
              >
                {companies.map((company) => (
                  <MenuItem
                    key={company.companyReference}
                    value={company.companyReference}
                    className={classes.menuItem}
                  >
                    {company.companyName}
                    {company.companyReference === selectedCompany && (
                      <CheckIcon className={classes.checkIcon} />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Actions */}
          <Box className={classes.actions}>
            <Button
              className={classes.primary}
              variant="contained"
              disableElevation
              onClick={handleSubmit}
              disabled={!selectedCompany || selectedCompany === currentCompany}
            >
              Submit
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
